import ErrorState from '@pinecast/common/ErrorState';
import LoadingState from '@pinecast/common/LoadingState';
import ChromeSidebar from '@pinecast/db-spa/App/ChromeSidebar';
import {PageTitleProvider} from '@pinecast/db-spa/App/usePageTitle';
import {useQuery} from '@pinecast/gql';
import type {SubscriptionsPage} from '@pinecast/gql/__generated_tj__/SubscriptionsPage';
import {defineMessage, FormattedMessage} from '@pinecast/i18n';
import {Navigate} from 'react-router';
import {PageHeader} from '@pinecast/common/Text';
import Section from '@pinecast/common/Section';

import Chrome from '../Chrome';

import ReceiptList from './ReceiptList';
import SettingsPageLanguage from './SettingsPageLanguage';
import SubscriptionPageHeader from './SubscriptionPageHeader';
import SubscriptionsPageRecurringDonationList from './SubscriptionsPageRecurringDonationList';
import {SUBSCRIPTIONS_PAGE} from './subscriptionsQuery';

const messages = {
  title: defineMessage({
    description: 'Title of the page. {subtitle} is the current page',
    defaultMessage: '{subtitle} - Podcast Contributions',
  }),
  receipts: defineMessage({
    description: 'Receipts tab of the tip jar',
    defaultMessage: 'Receipts',
  }),
  recurringDonations: defineMessage({
    description: 'Active tab of the tip jar',
    defaultMessage: 'Active subscriptions',
  }),
  settings: defineMessage({
    description: 'Settings tab of the tip jar',
    defaultMessage: 'Settings',
  }),
};

const SubscriptionsPageWrapper = ({children}: {children: React.ReactNode}) => {
  return (
    <PageTitleProvider message={messages.title}>
      <Chrome>
        <SubscriptionPageHeader />
        <ChromeSidebar
          links={[
            {
              exact: false,
              labelMessage: intl =>
                intl.formatMessage(messages.recurringDonations),
              path: '/subscriptions/recurring_tips',
            },
            {
              labelMessage: intl => intl.formatMessage(messages.receipts),
              path: '/subscriptions/receipts',
            },
            {
              labelMessage: intl => intl.formatMessage(messages.settings),
              path: '/subscriptions/settings',
            },
          ]}
        >
          {children}
        </ChromeSidebar>
      </Chrome>
    </PageTitleProvider>
  );
};

const useSubscriptionsPage = () => {
  const subscriptionsPage = useQuery<SubscriptionsPage>(SUBSCRIPTIONS_PAGE);

  const loadingState = subscriptionsPage.loading && (
    <LoadingState
      title={
        <FormattedMessage
          description="Loading message for tip jar subscriptions"
          defaultMessage="Loading subscriptions…"
        />
      }
    />
  );
  const errorState = subscriptionsPage.error && (
    <ErrorState
      title={
        <FormattedMessage
          description="Generic error message for tip jar subscriptions"
          defaultMessage="We could not load your subscriptions."
        />
      }
      copy={
        <FormattedMessage
          description="Generic error message copy for tip jar subscriptions"
          defaultMessage="We encountered a problem while trying to load your subscriptions."
        />
      }
      actionLabel={
        <FormattedMessage
          description="Generic error message retry button for tip jar subscriptions"
          defaultMessage="Retry"
        />
      }
      onAction={subscriptionsPage.refetch}
    />
  );
  return {
    loadingState,
    errorState,
    subscriptionsPage,
  };
};

export const SubscriptionsPageIndex = () => {
  const {loadingState, errorState, subscriptionsPage} = useSubscriptionsPage();

  return (
    <SubscriptionsPageWrapper>
      {loadingState ||
      errorState ||
      subscriptionsPage.data!.tipJarSubscriptions.length ? (
        <Navigate to="/subscriptions/recurring_tips" replace />
      ) : (
        <Navigate to="/subscriptions/receipts" replace />
      )}
    </SubscriptionsPageWrapper>
  );
};

export const SubscriptionsPageReceipts = () => {
  const {loadingState, errorState, subscriptionsPage} = useSubscriptionsPage();

  return (
    <SubscriptionsPageWrapper>
      <PageHeader>
        <FormattedMessage {...messages.receipts} />
      </PageHeader>
      <Section>
        {loadingState || errorState || (
          <ReceiptList events={subscriptionsPage.data!.tipJarTipEvents} />
        )}
      </Section>
    </SubscriptionsPageWrapper>
  );
};

export const SubscriptionsPageRecurringDonations = () => {
  const {loadingState, errorState, subscriptionsPage} = useSubscriptionsPage();

  return (
    <SubscriptionsPageWrapper>
      <PageHeader>
        <FormattedMessage {...messages.recurringDonations} />
      </PageHeader>
      <Section>
        {loadingState || errorState || (
          <SubscriptionsPageRecurringDonationList
            subscriptions={subscriptionsPage.data!.tipJarSubscriptions}
          />
        )}
      </Section>
    </SubscriptionsPageWrapper>
  );
};

export const SubscriptionsPageSettings = () => (
  <SubscriptionsPageWrapper>
    <PageHeader>
      <FormattedMessage {...messages.settings} />
    </PageHeader>
    <Section>
      <SettingsPageLanguage />
    </Section>
  </SubscriptionsPageWrapper>
);
